import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects'
import RoleBadge from '../layout/RoleBadge'
import api from '../../services/api.js'
import { isActiveProject } from '../../utils/helpers'
import { RecommendationsBar } from '../common/DashboardCharts.jsx'
import AIActionGuide from '../ai/AIActionGuide.jsx'
import AgentProposals from './AgentProposals.jsx'
import EmailIntakeLog from './EmailIntakeLog.jsx'
import { useTranslation } from 'react-i18next';

function ConsultantDashboard() {
  const { t } = useTranslation('dashboards');
  const { user } = useAuth()
  const navigate = useNavigate()
  const { projects } = useProjects()
  const [recommendations, setRecommendations] = useState([])
  const [contracts, setContracts] = useState([])
  const [impactScore, setImpactScore] = useState(0)
  const [onboarding, setOnboarding] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.allSettled([
      api.get('/consultant/recommendations/'),
      api.get('/contracts/'),
      api.get('/consultant/impact-score/'),
      api.get('/onboarding/checklist'),
    ]).then(([recsRes, contractsRes, impactRes, onboardingRes]) => {
      if (recsRes.status === 'fulfilled') setRecommendations(recsRes.value.data)
      if (contractsRes.status === 'fulfilled') setContracts(contractsRes.value.data)
      if (impactRes.status === 'fulfilled') setImpactScore(impactRes.value.data?.impact_value || 0)
      if (onboardingRes.status === 'fulfilled') setOnboarding(onboardingRes.value.data)
    }).finally(() => setLoading(false))
  }, [])

  // /projects/ is already backend-scoped to what this consultant may see — including projects
  // where they only own a milestone (not the primary consultant_id). Don't re-filter on
  // consultant_id, or milestone-only assignments (workshop/offer-to-more) wrongly vanish.
  const myProjects = Array.isArray(projects) ? projects : []
  // Same definition as every other dashboard: a project being planned is still active work.
  // This card is labelled with the same word as the Admin one and counted differently.
  const activeProjects = myProjects.filter(isActiveProject)
  const approvedRecs = recommendations.filter(r => r.status === 'Approved')
  const activeContract = contracts.find(c => c.status === 'Active')

  // Onboarding completion check — API returns array directly
  const checklistItems = Array.isArray(onboarding) ? onboarding : (onboarding?.items || [])
  const completedSteps = checklistItems.filter(i => i.completed).length
  const totalSteps = checklistItems.length || 5
  // KYC verification is an ADMIN step — don't nag the consultant about it.
  const myItems = checklistItems.filter(i => i.item_key !== 'kyc_verified')
  const myDone = myItems.filter(i => i.completed).length
  const myTotal = myItems.length || 4
  const isActive = (user?.consultant_status || '').toLowerCase() === 'active'
  // Fully done: admin activated, or every checklist item (incl. KYC) is complete.
  const onboardingComplete = isActive || (totalSteps > 0 && completedSteps === totalSteps)
  // Consultant has finished everything that is theirs to do — now waiting on admin.
  const awaitingVerification = !onboardingComplete && myTotal > 0 && myDone === myTotal

  const consultantStats = [
    {
      name: t('active_projects'),
      value: activeProjects.length,
      icon: '🚀',
    },
    {
      name: 'Recommendations',
      value: recommendations.length,
      icon: '💡',
    },
    {
      name: t('approved_ideas'),
      value: approvedRecs.length,
      icon: '✅',
    },
    {
      name: t('impact_value'),
      value: impactScore >= 1000 ? `$${(impactScore / 1000).toFixed(0)}K` : `$${Number(impactScore).toLocaleString()}`,
      icon: '💰',
    },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* AI Copilot action guide */}
      <AIActionGuide />
      {/* Prepared by the agent, not yet done — usually from an email. */}
      <AgentProposals />
      {/* What arrived by email and what became of it — collapsed until something is wrong. */}
      <EmailIntakeLog />
      {/* Header */}
      <div className="bg-linear-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{t('consultant_title')}</h1>
            <p className="mt-1 opacity-90">{t('consultant_subtitle')}</p>
            {user?.consultant_code && (
              <span className="inline-block mt-2 text-xs font-mono bg-white/20 px-2 py-1 rounded-md">
                ID: {user.consultant_code}
              </span>
            )}
          </div>
          <div className="text-left md:text-right">
            <p className="text-sm opacity-90">{t('specialization')}</p>
            <p className="text-lg font-semibold">{user?.specialization || 'Consulting'}</p>
            <RoleBadge role={user?.role} />
          </div>
        </div>
      </div>

      {/* v3.2: projects this consultant was promoted to LEAD — they're the sub-project PM here */}
      {(() => {
        const myId = user?.id ?? user?.user_id
        const managed = myProjects.filter(p => (p.projectManagerId ?? p.project_manager_id) === myId)
        if (!managed.length) return null
        return (
          <div className="bg-white border border-purple-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-purple-700 font-semibold">⭐ Projects you lead</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{t('subproject_pm')}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">You were promoted to lead these — add milestones, assign your own consultants and approve their work here.</p>
            <div className="space-y-2">
              {managed.map(p => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="w-full text-left flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-4 py-3 hover:border-purple-300 hover:bg-purple-50/40 transition"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.status}{p.project_number ? ` · ${p.project_number}` : ''}</p>
                  </div>
                  <span className="text-xs text-gray-500 shrink-0">{p.progress || 0}%</span>
                </button>
              ))}
            </div>
          </div>
        )
      })()}

      {/* Onboarding progress banner — only while the consultant still has steps to do */}
      {!loading && !onboardingComplete && !awaitingVerification && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-amber-100"
          onClick={() => navigate('/onboarding/my')}
        >
          <div>
            <p className="text-sm font-semibold text-amber-800">{t('complete_onboarding')}</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {myDone} of {myTotal} steps completed — finish to unlock contract signing.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-24 bg-amber-200 rounded-full h-2">
              <div
                className="bg-amber-500 h-2 rounded-full"
                style={{ width: `${myTotal > 0 ? (myDone / myTotal) * 100 : 0}%` }}
              />
            </div>
            <span className="text-xs text-amber-700 font-medium">
              {myTotal > 0 ? Math.round((myDone / myTotal) * 100) : 0}%
            </span>
          </div>
        </div>
      )}

      {/* Submitted everything — just waiting on admin verification (no action needed) */}
      {!loading && awaitingVerification && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <span className="text-lg">⏳</span>
          <div>
            <p className="text-sm font-semibold text-blue-800">{t('onboarding_submitted')}</p>
            <p className="text-xs text-blue-700 mt-0.5">
              You've completed all your steps. An admin is verifying your documents — no action needed.
            </p>
          </div>
        </div>
      )}

      {/* Active contract banner */}
      {!loading && activeContract && (
        <div
          className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-green-100"
          onClick={() => navigate(`/contracts/${activeContract.id}`)}
        >
          <div>
            <p className="text-sm font-semibold text-green-800">{t('active_contract')}</p>
            <p className="text-xs text-green-700 mt-0.5">
              {activeContract.contract_number} — ${Number(activeContract.total_fee || 0).toLocaleString()} total fee
            </p>
          </div>
          <span className="text-xs text-green-700 font-medium shrink-0">{t('view_arrow')}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {consultantStats.map((stat, index) => (
          <div key={index} className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className="text-2xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart (Phase C2) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecommendationsBar recommendations={recommendations} />
        {/* Could add an Impact-over-time line chart here once we track timestamps */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Projects */}
        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('consulting_projects')}</h3>
          </div>
          <div className="p-6">
            {myProjects.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">{t('no_projects_assigned')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myProjects.map((project, index) => {
                  const progress = project.progress || 0
                  return (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
                        <h4 className="font-medium text-gray-900">{project.name}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full w-fit ${
                          project.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          project.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {project.status}
                        </span>
                      </div>
                      {project.description && (
                        <p className="text-sm text-gray-500 mb-2 line-clamp-2">{project.description}</p>
                      )}
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-indigo-600 h-1.5 rounded-full"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Project progress: {progress}%</p>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* My Recommendations */}
        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('my_recommendations')}</h3>
              <button
                className="btn-primary text-sm"
                onClick={() => navigate('/consultant/recommendations')}
              >
                View All
              </button>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map(i => (
                  <div key={i} className="animate-pulse h-20 bg-gray-100 rounded-lg" />
                ))}
              </div>
            ) : recommendations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">{t('no_recommendations')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recommendations.slice(0, 4).map((rec, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-1 gap-2">
                      <h4 className="font-medium text-gray-900 text-sm leading-snug">{rec.title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                        rec.status === 'Approved' ? 'bg-green-100 text-green-800' :
                        rec.status === 'Under Review' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {rec.status}
                      </span>
                    </div>
                    {rec.description && (
                      <p className="text-xs text-gray-500 line-clamp-2">{rec.description}</p>
                    )}
                    {rec.implementation_cost && (
                      <p className="text-xs text-gray-400 mt-1">
                        Cost: ${Number(rec.implementation_cost).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConsultantDashboard
